import axios from "axios";
import { API_URL } from "../api";

// API_URL уже оканчивается на /api → база админки = /api/admin
export const ADMIN_API_URL = `${API_URL}/admin`;
export const ADMIN_TOKEN_KEY = "admin_token";

export function getAdminToken() {
  try { return localStorage.getItem(ADMIN_TOKEN_KEY) || ""; } catch (e) { return ""; }
}
export function setAdminToken(token) {
  try {
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
    else localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch (e) { /* ignore */ }
}

const http = axios.create({
  baseURL: ADMIN_API_URL,
  timeout: 60_000,
  withCredentials: false, // авторизация по токену, cookie сессии клиента не нужны
  headers: { Accept: "application/json" },
});

// Подставляем токен в каждый запрос
http.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) config.headers.Authorization = `Token ${token}`;
  return config;
});

// Нормализация ошибок (как в основном api.js) + авто-разлогин на 401
http.interceptors.response.use(
  (r) => r,
  (error) => {
    const data = error.response?.data;
    let message = "Неизвестная ошибка";
    if (typeof data === "string") message = data;
    else if (data?.error) message = data.error;
    else if (data?.detail) message = data.detail;
    else if (data && typeof data === "object") {
      message = Object.entries(data)
        .map(([f, e]) => `${f}: ${Array.isArray(e) ? e.join(", ") : e}`)
        .join(" | ");
    }

    if (error.response?.status === 401) {
      setAdminToken("");
      if (!String(window.location.pathname).endsWith("/admin/login")) {
        window.location.assign("/admin/login");
      }
    }

    const err = new Error(message);
    err.status = error.response?.status;
    err.data = data;
    return Promise.reject(err);
  }
);

export const adminApi = {
  // ── Авторизация ──
  async login(email, password) {
    const { data } = await http.post("/auth/login/", { email, password });
    return data; // { token, account }
  },
  async me() {
    const { data } = await http.get("/auth/me/");
    return data;
  },
  async logout() {
    const { data } = await http.post("/auth/logout/");
    return data;
  },
  async updateProfile(patch) {
    const { data } = await http.patch("/auth/profile/", patch);
    return data;
  },
  async changePassword(oldPassword, newPassword) {
    const { data } = await http.post("/auth/change-password/", {
      old_password: oldPassword, new_password: newPassword,
    });
    return data;
  },

  // ── Сессии ──
  async listSessions() { const { data } = await http.get("/auth/sessions/"); return data; },
  async terminateSession(id) { const { data } = await http.delete(`/auth/sessions/${id}/`); return data; },
  async terminateOtherSessions() { const { data } = await http.post("/auth/sessions/terminate-others/"); return data; },

  // ── Сотрудники (superadmin) ──
  async listStaff(role) {
    const { data } = await http.get("/staff/", { params: role ? { role } : {} });
    return data;
  },
  async getStaff(id) { const { data } = await http.get(`/staff/${id}/`); return data; },
  async createStaff(payload) { const { data } = await http.post("/staff/create/", payload); return data; },
  async updateStaff(id, patch) { const { data } = await http.patch(`/staff/${id}/update/`, patch); return data; },
  async resetStaffPassword(id, newPassword) {
    const { data } = await http.post(`/staff/${id}/reset-password/`, { new_password: newPassword });
    return data;
  },
  async deleteStaff(id) { const { data } = await http.delete(`/staff/${id}/delete/`); return data; },

  // ── Статистика ──
  async funnel(dateFrom, dateTo) {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const { data } = await http.get("/stats/funnel/", { params });
    return data;
  },
  async overview() { const { data } = await http.get("/stats/overview/"); return data; },
  async dashboardClients(dateFrom, dateTo) {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const { data } = await http.get("/stats/clients/", { params });
    return data;
  },
  async ordersStats(params) {
    const { data } = await http.get("/orders/stats/", { params });
    return data;
  },

  // ── Логи (по коду или UUID книги) ──
  async logsLookup(params) {
    const { data } = await http.get("/logs/", { params });
    return data;
  },
  async logsExportBlob(params) {
    // Полный ответ: res.data (Blob), res.headers — для скачивания файла
    const res = await http.get("/logs/export/", { params, responseType: "blob" });
    return res;
  },

  // ── Заказы ──
  async ordersList(params) {
    const { data } = await http.get("/orders/", { params });
    return data;
  },
  async orderDetail(id) {
    const { data } = await http.get(`/orders/${id}/`);
    return data;
  },

  // ── Доп. действия (management-команды) ──
  async actionsCatalog() {
    const { data } = await http.get("/actions/");
    return data;
  },
  async actionsRun(action, params) {
    const { data } = await http.post("/actions/run/", { action, params });
    return data;
  },
};